import React from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'

export default function Show({
  account,
  accounts,
  navigation,
  stripe_connect,
  is_account_admin
}) {
  const {
    stripe_connected,
    onboarding_started,
    onboarding_complete,
    charges_enabled,
    payouts_enabled,
    can_accept_orders,
    requirements_pending,
    requirements_due,
    action_required,
    action_deadline,
    can_disconnect,
    platform_fee_percentage
  } = stripe_connect

  const handleConnect = () => {
    // Use form submission for POST to allow external redirect
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/manage/stripe_connect'
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'authenticity_token'
      input.value = csrfToken
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.submit()
  }

  const handleContinueSetup = () => {
    // Full page navigation for external Stripe redirect
    window.location.href = '/manage/stripe_connect/refresh'
  }

  const handleDisconnect = (e) => {
    e.preventDefault()
    if (confirm('Are you sure you want to disconnect your Stripe account? You will no longer be able to accept payments until you reconnect.')) {
      router.delete('/manage/stripe_connect')
    }
  }

  const formatRequirement = (req) => {
    const labels = {
      'business_profile.url': 'Business website',
      'business_profile.product_description': 'Business description',
      'external_account': 'Bank account',
      'individual.verification.document': 'Identity verification',
      'company.verification.document': 'Company verification',
      'tos_acceptance.date': 'Terms of service acceptance',
      'representative.verification.document': 'Representative verification'
    }
    return labels[req] || req.replace(/_/g, ' ').replace(/\./g, ' - ')
  }

  const formatDeadline = (isoDate) => {
    if (!isoDate) return null
    const date = new Date(isoDate)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  // Not started - show connect button
  if (!onboarding_started) {
    return (
      <Layout account={account} accounts={accounts} navigation={navigation} title="Payments">
        <div className="max-w-2xl">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Payment Setup Required</h3>
              <p className="mt-1 text-sm text-gray-500">
                Connect your Stripe account to start accepting online orders
              </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  To accept payments from customers, you need to connect a Stripe account.
                  Stripe handles all payment processing securely.
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Receive payments directly to your bank account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>View payouts and transactions in your Stripe dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Platform fee: {platform_fee_percentage}% per transaction</span>
                  </li>
                </ul>
                <button
                  onClick={handleConnect}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Connect Stripe Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  // Onboarding incomplete
  if (!onboarding_complete) {
    const currentlyDue = requirements_due?.currently_due || []
    const eventuallyDue = requirements_due?.eventually_due || []

    return (
      <Layout account={account} accounts={accounts} navigation={navigation} title="Payments">
        <div className="max-w-2xl">
          <div className="bg-white shadow rounded-lg overflow-hidden border-l-4 border-yellow-400">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <WarningIcon className="h-5 w-5 text-yellow-500" />
                <h3 className="text-lg font-medium text-gray-900">Complete Payment Setup</h3>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Your Stripe account setup is incomplete
              </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                {currentlyDue.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Required now:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {currentlyDue.map((req) => (
                        <li key={req} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full" />
                          {formatRequirement(req)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {eventuallyDue.length > 0 && currentlyDue.length === 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Additional information needed:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {eventuallyDue.slice(0, 5).map((req) => (
                        <li key={req} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                          {formatRequirement(req)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  onClick={handleContinueSetup}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  Continue Setup
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  // Fully connected
  return (
    <Layout account={account} accounts={accounts} navigation={navigation} title="Payments">
      <div className="max-w-2xl space-y-6">
        {/* Status Card */}
        <div className={`bg-white shadow rounded-lg overflow-hidden border-l-4 ${can_accept_orders ? 'border-green-400' : 'border-yellow-400'}`}>
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {can_accept_orders ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              ) : (
                <WarningIcon className="h-5 w-5 text-yellow-500" />
              )}
              <h3 className="text-lg font-medium text-gray-900">
                {can_accept_orders ? 'Payments Active' : 'Payments Pending'}
              </h3>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <StatusRow label="Charges" enabled={charges_enabled} />
                <StatusRow label="Payouts" enabled={payouts_enabled} />
              </div>

              {action_required && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Action required:</strong> Additional information is needed.
                    {action_deadline && (
                      <span> Please complete by {formatDeadline(action_deadline)}.</span>
                    )}
                  </p>
                  <button
                    onClick={handleContinueSetup}
                    className="mt-2 text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
                  >
                    Update information
                  </button>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href="/manage/stripe_connect/dashboard"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  View Stripe Dashboard
                </a>
                {is_account_admin && can_disconnect && (
                  <button
                    onClick={handleDisconnect}
                    className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Disconnect
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fee Information */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Fee Information</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Platform fee</dt>
                <dd className="text-gray-900 font-medium">{platform_fee_percentage}%</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Stripe processing (UK cards)</dt>
                <dd className="text-gray-900 font-medium">1.5% + 20p</dd>
              </div>
              <p className="text-xs text-gray-500 pt-2">
                Stripe fees are deducted from the payment. Platform fee is taken from your share.
                View detailed transaction fees in your Stripe dashboard.
              </p>
            </dl>
          </div>
        </div>
      </div>
    </Layout>
  )
}

function StatusRow({ label, enabled }) {
  return (
    <div className="flex items-center gap-2">
      {enabled ? (
        <CheckCircleIcon className="h-4 w-4 text-green-500" />
      ) : (
        <XCircleIcon className="h-4 w-4 text-red-500" />
      )}
      <span className="text-sm text-gray-600">{label}</span>
    </div>
  )
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  )
}

function XCircleIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  )
}

function WarningIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  )
}
